import React, { Component, createRef } from 'react';

import { withSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';

import OfferBreadcrumbs from '~/components/Common/Elements/OfferBreadcrumbs';
import Spinner from '~/components/Common/Spinner';

import { OFFER_TYPE_HOTEL, OFFER_TYPE_LAST_MINUTE_HOTEL, OFFER_TYPE_TACTICAL_AO_HOTEL } from '~/consts/offerTypes';

import featureToggle from '~/utils/featureToggle';
import { getHotelUniquePropertyDetails } from '~/utils/hotelUtils';

import OffersService from '../../../services/OffersService';
import { reportError } from '../../../utils/reportError';
import ImagesForm from '../../Common/Forms/ImagesForm';

import ApiListContainer from './ApiListContainer';
import CopyOfferImagesModal from './CopyOfferImagesModal';
import LpcImageSyncControls from './LpcImageSyncControls';

class ImagesEditContainer extends Component {
  constructor(props) {
    super(props);

    const { match } = props;
    const { id_offer: offerId } = match.params;

    this.state = {
      isCopyModalVisible: false,
      isUploadingPending: false,
      images: [],
      offerDetails: {},
      offerId: offerId,
      pendingUploadCount: 0,
      isReady: false,
      propertyDetails: {},
      propertyImages: [],
      fullPropertyImages: [],
      overridableContent: false,
      isLpcOffer: false,
    };

    this.imagesFormRef = createRef();
  }

  filterImages = (fullPropertyImages, mappedImages) => {
    const mappedImageIds = new Set(mappedImages.map((image) => image.id_cloudinary_external));
    return fullPropertyImages.filter((image) => !mappedImageIds.has(image.id_cloudinary_external));
  };

  fetchData = () => {
    Promise.all([OffersService.getImages(this.state.offerId), OffersService.getOffer(this.state.offerId)])
      .then(async ([offerImagesRaw, offer]) => {
        const offerImages = offerImagesRaw.result.map((image) =>
          Object.assign({}, image, {
            id: image.id_image,
          }),
        );

        this.originalOfferDetails = offer.result;
        const propertyDetails = await getHotelUniquePropertyDetails(this.originalOfferDetails);
        let propertyImages = [];
        let fullPropertyImages = [];
        if (propertyDetails) {
          fullPropertyImages = propertyDetails.images;
          propertyImages =
            this.filterImages(fullPropertyImages, offerImages).map((image) => ({ ...image, selected: false })) ?? [];
        }
        const isHotelOffer = [OFFER_TYPE_TACTICAL_AO_HOTEL, OFFER_TYPE_HOTEL, OFFER_TYPE_LAST_MINUTE_HOTEL].includes(
          this.originalOfferDetails.type,
        );
        const isLpcOffer = this.originalOfferDetails.type === OFFER_TYPE_TACTICAL_AO_HOTEL;
        const overridableContent = featureToggle.availableToShow('ADDITIONAL_PROPERTY_CONTENT_ENABLED') && isHotelOffer;

        this.setState({
          offerDetails: offer.result,
          propertyDetails,
          images: offerImages,
          fullPropertyImages: fullPropertyImages,
          propertyImages: propertyImages,
          isReady: true,
          overridableContent,
          isLpcOffer,
        });
      })
      .catch((error) => {
        reportError(error);
      });
  };

  onAddImage = async (cloudinaryId, filename, desiredOrder) => {
    const uploadedImage = await OffersService.createImage(
      {
        id_cloudinary_external: cloudinaryId,
        filename: filename ? filename : undefined,
      },
      this.state.offerId,
    ).then((newImage) => {
      newImage.id = newImage.id_image;
      return newImage;
    });
    let normalizedUploadedImage;
    await new Promise((resolve) => {
      this.setState(
        (previousState) => {
          const updatedPropertyImages = previousState.propertyImages.filter(
            (image) => image.id_cloudinary_external !== cloudinaryId,
          );
          const nextOrder = desiredOrder ?? previousState.images.length + 1;
          normalizedUploadedImage = {
            ...uploadedImage,
            id: uploadedImage.id ?? uploadedImage.id_image,
            id_cloudinary_external: uploadedImage.id_cloudinary_external ?? cloudinaryId,
            filename: uploadedImage.filename ?? filename,
            order: nextOrder,
          };
          const offerImages = previousState.images.concat(normalizedUploadedImage);
          return {
            propertyImages: updatedPropertyImages,
            images: offerImages,
          };
        },
        () => resolve(undefined),
      );
    });
    return normalizedUploadedImage ?? uploadedImage;
  };

  onUpdateImages = async (newImageList) => {
    const updatePayload = newImageList.map((image) => ({
      id_image: image.id,
      id_cloudinary_external: image.id_cloudinary_external,
      order: image.order,
      title: image.title,
      hidden: image.hidden,
    }));

    this.setState({
      images: newImageList,
    });

    return await OffersService.updateImages(updatePayload, this.state.offerId);
  };

  onDeleteImage = async (imageId) => {
    const newImages = this.state.images.filter((i) => i.id !== imageId);
    newImages.forEach((image, index) => {
      image.order = index + 1;
    });
    const propertyImages = this.filterImages(this.state.fullPropertyImages, newImages);
    this.setState({
      images: newImages,
      propertyImages,
    });
    return await OffersService.deleteImage(this.state.offerId, imageId);
  };

  // This function is used to toggle the selection of an image within an array of images
  // We loop over all images, only adjusting the image of concern, otherwise leaving the selected value as is
  toggleImageSelection = (id, currentSelectedValue) => {
    const newImages = this.state.propertyImages.map((p) => ({
      ...p,
      selected: p.id === id ? !currentSelectedValue : p.selected,
    }));
    this.setState({ propertyImages: newImages });
  };

  copyImagesToOffer = () => {
    this.state.propertyImages
      .filter((i) => i.selected)
      .map((p) => this.onAddImage(p.id_cloudinary_external, p.filename));
  };

  selectAllPropertyImages = () => {
    const newImages = this.state.propertyImages.map((p) => ({
      ...p,
      selected: true,
    }));
    this.setState({ propertyImages: newImages });
  };

  showCopyModal = () => {
    this.setState({
      isCopyModalVisible: true,
    });
  };

  copyOfferImagesData = () => {
    OffersService.getImages(this.state.offerId)
      .then((images) => {
        const mappedImages = images.result.map((image) =>
          Object.assign({}, image, {
            id: image.id_image,
          }),
        );

        this.setState({
          images: mappedImages,
          isReady: true,
        });

        this.hideCopyModal();
      })
      .catch((error) => {
        reportError(error);
      });
  };

  hideCopyModal = () => {
    this.setState({
      isCopyModalVisible: false,
    });
  };

  componentDidMount = () => {
    this.fetchData();
  };

  setAsNotReady = () => {
    this.setState({
      isReady: false,
    });
  };

  handlePendingUploadStateChange = ({ isUploadingPending, pendingUploadCount }) => {
    this.setState({
      isUploadingPending,
      pendingUploadCount,
    });
  };

  render() {
    const { isUploadingPending, offerId, images, isCopyModalVisible, isReady, isLpcOffer, pendingUploadCount } =
      this.state;

    const formImages = images.map((i) => ({
      ...i,
      publicImageId: i.id_cloudinary_external,
    }));

    return (
      <Box
        className="offer-edit container"
        sx={{
          px: {
            xs: 2,
            md: 3,
          },
        }}
      >
        <Helmet>
          <title>Offers | {this.state.offerDetails?.name || offerId} | Edit Offer Images</title>
        </Helmet>
        <OfferBreadcrumbs offerId={offerId} currentPage="Images" />
        <h1 className="page-header">
          Edit Offer Images
          <Button
            startIcon={<ContentCopyIcon />}
            onClick={this.showCopyModal}
            className="copy-button"
            aria-label="Copy Offer"
          >
            Copy
          </Button>
          {isLpcOffer && (
            <LpcImageSyncControls
              propertyId={this.state.propertyDetails?.id}
              propertyName={this.state.propertyDetails?.name}
              offerImages={images}
              onAddImage={this.onAddImage}
            />
          )}
        </h1>
        {isReady && (
          <Box>
            <ImagesForm
              ref={this.imagesFormRef}
              enablePendingCropUpload
              hasHiddenToggle
              images={formImages}
              onAddImage={this.onAddImage}
              onUpdateImages={this.onUpdateImages}
              onDeleteImage={this.onDeleteImage}
              onPendingUploadStateChange={this.handlePendingUploadStateChange}
            />
            <CopyOfferImagesModal
              isCopyModalVisible={isCopyModalVisible}
              setAsNotReady={this.setAsNotReady}
              hideCopyModal={this.hideCopyModal}
              copyOfferImagesData={this.copyOfferImagesData}
              onAddImage={this.onAddImage}
              currentOfferStatus={this.state.offerDetails.status}
            />
            {this.state.overridableContent && Object.keys(this.state.propertyImages).length > 0 && (
              <>
                <Box mt={7}>
                  <Typography variant="h6">Available Image(s) from property to offer</Typography>
                </Box>
                <ApiListContainer images={this.state.propertyImages} toggleImageSelection={this.toggleImageSelection} />
                <Box sx={{ justifyContent: 'end' }}>
                  <Button variant="contained" color="primary" onClick={this.copyImagesToOffer}>
                    Copy to Offer images
                  </Button>

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={this.selectAllPropertyImages}
                    style={{ marginLeft: '10px' }}
                  >
                    Select All
                  </Button>
                </Box>
              </>
            )}

            {this.state.overridableContent && !Object.keys(this.state.fullPropertyImages).length && (
              <Typography> No property has been linked to the offer yet!</Typography>
            )}

            {pendingUploadCount > 1 ? (
              <Box position="sticky" bottom={0} zIndex={2} mt={2}>
                <Divider />
                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  spacing={2}
                  py={2}
                  sx={{ backgroundColor: 'background.paper' }}
                >
                  <Button
                    disabled={isUploadingPending}
                    variant="contained"
                    onClick={() => this.imagesFormRef.current?.uploadAllPending?.()}
                  >
                    Upload all images
                  </Button>
                </Stack>
              </Box>
            ) : null}
          </Box>
        )}
        {!isReady && <Spinner />}
      </Box>
    );
  }
}

export default withSnackbar(ImagesEditContainer);
